export type Json =
  | string
  | number
  | boolean
  | null
  | { [key: string]: Json | undefined }
  | Json[]

export type Database = {
  // Allows to automatically instantiate createClient with right options
  // instead of createClient<Database, { PostgrestVersion: 'XX' }>(URL, KEY)
  __InternalSupabase: {
    PostgrestVersion: "14.5"
  }
  public: {
    Tables: {
      academic_documents: {
        Row: {
          author_id: string
          category: string
          created_at: string
          description: string | null
          expires_at: string | null
          file_url: string | null
          id: string
          published: boolean
          target_classes: string[] | null
          title: string
          updated_at: string
        }
        Insert: {
          author_id: string
          category?: string
          created_at?: string
          description?: string | null
          expires_at?: string | null
          file_url?: string | null
          id?: string
          published?: boolean
          target_classes?: string[] | null
          title: string
          updated_at?: string
        }
        Update: {
          author_id?: string
          category?: string
          created_at?: string
          description?: string | null
          expires_at?: string | null
          file_url?: string | null
          id?: string
          published?: boolean
          target_classes?: string[] | null
          title?: string
          updated_at?: string
        }
        Relationships: []
      }
      announcements: {
        Row: {
          author_id: string
          category: string
          content: string
          created_at: string
          expires_at: string | null
          file_url: string | null
          id: string
          priority: string
          published: boolean
          target_classes: string[] | null
          title: string
          updated_at: string
        }
        Insert: {
          author_id: string
          category?: string
          content: string
          created_at?: string
          expires_at?: string | null
          file_url?: string | null
          id?: string
          priority?: string
          published?: boolean
          target_classes?: string[] | null
          title: string
          updated_at?: string
        }
        Update: {
          author_id?: string
          category?: string
          content?: string
          created_at?: string
          expires_at?: string | null
          file_url?: string | null
          id?: string
          priority?: string
          published?: boolean
          target_classes?: string[] | null
          title?: string
          updated_at?: string
        }
        Relationships: []
      }
      applications: {
        Row: {
          applicant_name: string
          created_at: string
          documents: Json | null
          email: string
          id: string
          notes: string | null
          phone: string | null
          program_applied: string
          reviewed_by: string | null
          status: string
          updated_at: string
        }
        Insert: {
          applicant_name: string
          created_at?: string
          documents?: Json | null
          email: string
          id?: string
          notes?: string | null
          phone?: string | null
          program_applied: string
          reviewed_by?: string | null
          status?: string
          updated_at?: string
        }
        Update: {
          applicant_name?: string
          created_at?: string
          documents?: Json | null
          email?: string
          id?: string
          notes?: string | null
          phone?: string | null
          program_applied?: string
          reviewed_by?: string | null
          status?: string
          updated_at?: string
        }
        Relationships: []
      }
      approvals: {
        Row: {
          created_at: string
          id: string
          record_id: string
          requested_by: string
          review_notes: string | null
          reviewed_at: string | null
          reviewed_by: string | null
          status: string
          type: string
        }
        Insert: {
          created_at?: string
          id?: string
          record_id: string
          requested_by: string
          review_notes?: string | null
          reviewed_at?: string | null
          reviewed_by?: string | null
          status?: string
          type: string
        }
        Update: {
          created_at?: string
          id?: string
          record_id?: string
          requested_by?: string
          review_notes?: string | null
          reviewed_at?: string | null
          reviewed_by?: string | null
          status?: string
          type?: string
        }
        Relationships: []
      }
      attendance_records: {
        Row: {
          approved_at: string | null
          approved_by: string | null
          attendance_date: string
          attendance_type: string
          created_at: string
          field_group_id: string | null
          geo_lat: number | null
          geo_lng: number | null
          id: string
          notes: string | null
          registration_number: string | null
          rejection_reason: string | null
          scanned_at: string
          scanned_by: string | null
          session_id: string | null
          signature_url: string | null
          status: string
          student_id: string
          time_out: string | null
        }
        Insert: {
          approved_at?: string | null
          approved_by?: string | null
          attendance_date?: string
          attendance_type?: string
          created_at?: string
          field_group_id?: string | null
          geo_lat?: number | null
          geo_lng?: number | null
          id?: string
          notes?: string | null
          registration_number?: string | null
          rejection_reason?: string | null
          scanned_at?: string
          scanned_by?: string | null
          session_id?: string | null
          signature_url?: string | null
          status?: string
          student_id: string
          time_out?: string | null
        }
        Update: {
          approved_at?: string | null
          approved_by?: string | null
          attendance_date?: string
          attendance_type?: string
          created_at?: string
          field_group_id?: string | null
          geo_lat?: number | null
          geo_lng?: number | null
          id?: string
          notes?: string | null
          registration_number?: string | null
          rejection_reason?: string | null
          scanned_at?: string
          scanned_by?: string | null
          session_id?: string | null
          signature_url?: string | null
          status?: string
          student_id?: string
          time_out?: string | null
        }
        Relationships: [
          {
            foreignKeyName: "attendance_records_field_group_id_fkey"
            columns: ["field_group_id"]
            isOneToOne: false
            referencedRelation: "field_groups"
            referencedColumns: ["id"]
          },
          {
            foreignKeyName: "attendance_records_session_id_fkey"
            columns: ["session_id"]
            isOneToOne: false
            referencedRelation: "attendance_sessions"
            referencedColumns: ["id"]
          },
          {
            foreignKeyName: "attendance_records_student_id_fkey"
            columns: ["student_id"]
            isOneToOne: false
            referencedRelation: "students"
            referencedColumns: ["id"]
          },
        ]
      }
      attendance_sessions: {
        Row: {
          class: string | null
          closed_at: string | null
          created_at: string
          created_by: string
          excluded_from_coverage: boolean
          id: string
          module_code: string | null
          module_session_id: string | null
          opened_at: string | null
          remedial_parent_id: string | null
          session_date: string
          status: string
          topic: string | null
          updated_at: string
        }
        Insert: {
          class?: string | null
          closed_at?: string | null
          created_at?: string
          created_by: string
          excluded_from_coverage?: boolean
          id?: string
          module_code?: string | null
          module_session_id?: string | null
          opened_at?: string | null
          remedial_parent_id?: string | null
          session_date?: string
          status?: string
          topic?: string | null
          updated_at?: string
        }
        Update: {
          class?: string | null
          closed_at?: string | null
          created_at?: string
          created_by?: string
          excluded_from_coverage?: boolean
          id?: string
          module_code?: string | null
          module_session_id?: string | null
          opened_at?: string | null
          remedial_parent_id?: string | null
          session_date?: string
          status?: string
          topic?: string | null
          updated_at?: string
        }
        Relationships: [
          {
            foreignKeyName: "attendance_sessions_module_session_id_fkey"
            columns: ["module_session_id"]
            isOneToOne: false
            referencedRelation: "module_sessions"
            referencedColumns: ["id"]
          },
          {
            foreignKeyName: "attendance_sessions_remedial_parent_id_fkey"
            columns: ["remedial_parent_id"]
            isOneToOne: false
            referencedRelation: "attendance_sessions"
            referencedColumns: ["id"]
          },
        ]
      }
      audit_logs: {
        Row: {
          action: string
          created_at: string
          id: string
          new_data: Json | null
          old_data: Json | null
          record_id: string | null
          table_name: string
          user_id: string | null
        }
        Insert: {
          action: string
          created_at?: string
          id?: string
          new_data?: Json | null
          old_data?: Json | null
          record_id?: string | null
          table_name: string
          user_id?: string | null
        }
        Update: {
          action?: string
          created_at?: string
          id?: string
          new_data?: Json | null
          old_data?: Json | null
          record_id?: string | null
          table_name?: string
          user_id?: string | null
        }
        Relationships: []
      }
      chitchat_blocks: {
        Row: {
          blocked_id: string
          blocker_id: string
          created_at: string
          id: string
        }
        Insert: {
          blocked_id: string
          blocker_id: string
          created_at?: string
          id?: string
        }
        Update: {
          blocked_id?: string
          blocker_id?: string
          created_at?: string
          id?: string
        }
        Relationships: []
      }
      chitchat_conversations: {
        Row: {
          avatar_url: string | null
          created_at: string
          created_by: string
          description: string | null
          id: string
          is_group: boolean
          last_message_at: string
          name: string | null
          updated_at: string
        }
        Insert: {
          avatar_url?: string | null
          created_at?: string
          created_by: string
          description?: string | null
          id?: string
          is_group?: boolean
          last_message_at?: string
          name?: string | null
          updated_at?: string
        }
        Update: {
          avatar_url?: string | null
          created_at?: string
          created_by?: string
          description?: string | null
          id?: string
          is_group?: boolean
          last_message_at?: string
          name?: string | null
          updated_at?: string
        }
        Relationships: []
      }
      chitchat_devices: {
        Row: {
          auth: string
          created_at: string
          endpoint: string
          id: string
          p256dh: string
          user_agent: string | null
          user_id: string
        }
        Insert: {
          auth: string
          created_at?: string
          endpoint: string
          id?: string
          p256dh: string
          user_agent?: string | null
          user_id: string
        }
        Update: {
          auth?: string
          created_at?: string
          endpoint?: string
          id?: string
          p256dh?: string
          user_agent?: string | null
          user_id?: string
        }
        Relationships: []
      }
      chitchat_members: {
        Row: {
          archived: boolean
          cleared_at: string | null
          conversation_id: string
          id: string
          joined_at: string
          last_read_at: string
          muted: boolean
          notifications_enabled: boolean
          pinned: boolean
          role: string
          user_id: string
        }
        Insert: {
          archived?: boolean
          cleared_at?: string | null
          conversation_id: string
          id?: string
          joined_at?: string
          last_read_at?: string
          muted?: boolean
          notifications_enabled?: boolean
          pinned?: boolean
          role?: string
          user_id: string
        }
        Update: {
          archived?: boolean
          cleared_at?: string | null
          conversation_id?: string
          id?: string
          joined_at?: string
          last_read_at?: string
          muted?: boolean
          notifications_enabled?: boolean
          pinned?: boolean
          role?: string
          user_id?: string
        }
        Relationships: [
          {
            foreignKeyName: "chitchat_members_conversation_id_fkey"
            columns: ["conversation_id"]
            isOneToOne: false
            referencedRelation: "chitchat_conversations"
            referencedColumns: ["id"]
          },
        ]
      }
      chitchat_message_reactions: {
        Row: {
          created_at: string
          emoji: string
          id: string
          message_id: string
          user_id: string
        }
        Insert: {
          created_at?: string
          emoji: string
          id?: string
          message_id: string
          user_id: string
        }
        Update: {
          created_at?: string
          emoji?: string
          id?: string
          message_id?: string
          user_id?: string
        }
        Relationships: [
          {
            foreignKeyName: "chitchat_message_reactions_message_id_fkey"
            columns: ["message_id"]
            isOneToOne: false
            referencedRelation: "chitchat_messages"
            referencedColumns: ["id"]
          },
        ]
      }
      chitchat_messages: {
        Row: {
          attachment_name: string | null
          attachment_type: string | null
          attachment_url: string | null
          client_uuid: string | null
          content: string | null
          conversation_id: string
          created_at: string
          deleted: boolean
          deleted_for: string[]
          deleted_for_everyone: boolean
          edited: boolean
          forwarded: boolean
          id: string
          message_type: string
          reply_to_id: string | null
          sender_id: string
          updated_at: string
        }
        Insert: {
          attachment_name?: string | null
          attachment_type?: string | null
          attachment_url?: string | null
          client_uuid?: string | null
          content?: string | null
          conversation_id: string
          created_at?: string
          deleted?: boolean
          deleted_for?: string[]
          deleted_for_everyone?: boolean
          edited?: boolean
          forwarded?: boolean
          id?: string
          message_type?: string
          reply_to_id?: string | null
          sender_id: string
          updated_at?: string
        }
        Update: {
          attachment_name?: string | null
          attachment_type?: string | null
          attachment_url?: string | null
          client_uuid?: string | null
          content?: string | null
          conversation_id?: string
          created_at?: string
          deleted?: boolean
          deleted_for?: string[]
          deleted_for_everyone?: boolean
          edited?: boolean
          forwarded?: boolean
          id?: string
          message_type?: string
          reply_to_id?: string | null
          sender_id?: string
          updated_at?: string
        }
        Relationships: [
          {
            foreignKeyName: "chitchat_messages_conversation_id_fkey"
            columns: ["conversation_id"]
            isOneToOne: false
            referencedRelation: "chitchat_conversations"
            referencedColumns: ["id"]
          },
          {
            foreignKeyName: "chitchat_messages_reply_to_id_fkey"
            columns: ["reply_to_id"]
            isOneToOne: false
            referencedRelation: "chitchat_messages"
            referencedColumns: ["id"]
          },
        ]
      }
      chitchat_profiles: {
        Row: {
          avatar_url: string | null
          bio: string | null
          created_at: string
          display_name: string | null
          last_seen: string
          online: boolean
          phone: string | null
          privacy_last_seen: string
          privacy_photo: string
          privacy_status: string
          read_receipts_enabled: boolean
          theme: string
          updated_at: string
          user_id: string
          username: string | null
        }
        Insert: {
          avatar_url?: string | null
          bio?: string | null
          created_at?: string
          display_name?: string | null
          last_seen?: string
          online?: boolean
          phone?: string | null
          privacy_last_seen?: string
          privacy_photo?: string
          privacy_status?: string
          read_receipts_enabled?: boolean
          theme?: string
          updated_at?: string
          user_id: string
          username?: string | null
        }
        Update: {
          avatar_url?: string | null
          bio?: string | null
          created_at?: string
          display_name?: string | null
          last_seen?: string
          online?: boolean
          phone?: string | null
          privacy_last_seen?: string
          privacy_photo?: string
          privacy_status?: string
          read_receipts_enabled?: boolean
          theme?: string
          updated_at?: string
          user_id?: string
          username?: string | null
        }
        Relationships: []
      }
      chitchat_receipts: {
        Row: {
          conversation_id: string
          delivered_at: string | null
          id: string
          message_id: string
          read_at: string | null
          user_id: string
        }
        Insert: {
          conversation_id: string
          delivered_at?: string | null
          id?: string
          message_id: string
          read_at?: string | null
          user_id: string
        }
        Update: {
          conversation_id?: string
          delivered_at?: string | null
          id?: string
          message_id?: string
          read_at?: string | null
          user_id?: string
        }
        Relationships: [
          {
            foreignKeyName: "chitchat_receipts_conversation_id_fkey"
            columns: ["conversation_id"]
            isOneToOne: false
            referencedRelation: "chitchat_conversations"
            referencedColumns: ["id"]
          },
          {
            foreignKeyName: "chitchat_receipts_message_id_fkey"
            columns: ["message_id"]
            isOneToOne: false
            referencedRelation: "chitchat_messages"
            referencedColumns: ["id"]
          },
        ]
      }
      chitchat_status_audience: {
        Row: {
          id: string
          mode: string
          status_id: string
          user_id: string
        }
        Insert: {
          id?: string
          mode: string
          status_id: string
          user_id: string
        }
        Update: {
          id?: string
          mode?: string
          status_id?: string
          user_id?: string
        }
        Relationships: [
          {
            foreignKeyName: "chitchat_status_audience_status_id_fkey"
            columns: ["status_id"]
            isOneToOne: false
            referencedRelation: "chitchat_statuses"
            referencedColumns: ["id"]
          },
        ]
      }
      chitchat_status_views: {
        Row: {
          id: string
          status_id: string
          viewed_at: string
          viewer_id: string
        }
        Insert: {
          id?: string
          status_id: string
          viewed_at?: string
          viewer_id: string
        }
        Update: {
          id?: string
          status_id?: string
          viewed_at?: string
          viewer_id?: string
        }
        Relationships: [
          {
            foreignKeyName: "chitchat_status_views_status_id_fkey"
            columns: ["status_id"]
            isOneToOne: false
            referencedRelation: "chitchat_statuses"
            referencedColumns: ["id"]
          },
        ]
      }
      chitchat_statuses: {
        Row: {
          bg_color: string | null
          content: string | null
          created_at: string
          expires_at: string
          id: string
          media_type: string
          media_url: string | null
          privacy: string
          user_id: string
        }
        Insert: {
          bg_color?: string | null
          content?: string | null
          created_at?: string
          expires_at?: string
          id?: string
          media_type: string
          media_url?: string | null
          privacy?: string
          user_id: string
        }
        Update: {
          bg_color?: string | null
          content?: string | null
          created_at?: string
          expires_at?: string
          id?: string
          media_type?: string
          media_url?: string | null
          privacy?: string
          user_id?: string
        }
        Relationships: []
      }
      chitchat_typing: {
        Row: {
          conversation_id: string
          updated_at: string
          user_id: string
        }
        Insert: {
          conversation_id: string
          updated_at?: string
          user_id: string
        }
        Update: {
          conversation_id?: string
          updated_at?: string
          user_id?: string
        }
        Relationships: [
          {
            foreignKeyName: "chitchat_typing_conversation_id_fkey"
            columns: ["conversation_id"]
            isOneToOne: false
            referencedRelation: "chitchat_conversations"
            referencedColumns: ["id"]
          },
        ]
      }
      courses: {
        Row: {
          class: string | null
          course_code: string
          course_name: string
          created_at: string
          credits: number | null
          description: string | null
          id: string
          level: string | null
          program: string | null
          semester: string | null
        }
        Insert: {
          class?: string | null
          course_code: string
          course_name: string
          created_at?: string
          credits?: number | null
          description?: string | null
          id?: string
          level?: string | null
          program?: string | null
          semester?: string | null
        }
        Update: {
          class?: string | null
          course_code?: string
          course_name?: string
          created_at?: string
          credits?: number | null
          description?: string | null
          id?: string
          level?: string | null
          program?: string | null
          semester?: string | null
        }
        Relationships: []
      }
      custom_attendance_tasks: {
        Row: {
          assigned_to_role: string
          attendance_date: string
          created_at: string
          created_by: string
          id: string
          notes: string | null
          status: string
          updated_at: string
        }
        Insert: {
          assigned_to_role?: string
          attendance_date: string
          created_at?: string
          created_by: string
          id?: string
          notes?: string | null
          status?: string
          updated_at?: string
        }
        Update: {
          assigned_to_role?: string
          attendance_date?: string
          created_at?: string
          created_by?: string
          id?: string
          notes?: string | null
          status?: string
          updated_at?: string
        }
        Relationships: []
      }
      discipline_cases: {
        Row: {
          category: string | null
          confidential_notes: string | null
          created_at: string
          description: string
          hearing_date: string | null
          id: string
          incident_date: string
          outcome: string | null
          reported_by: string | null
          sanction: string | null
          severity: string | null
          status: string
          student_id: string
          updated_at: string
        }
        Insert: {
          category?: string | null
          confidential_notes?: string | null
          created_at?: string
          description: string
          hearing_date?: string | null
          id?: string
          incident_date: string
          outcome?: string | null
          reported_by?: string | null
          sanction?: string | null
          severity?: string | null
          status?: string
          student_id: string
          updated_at?: string
        }
        Update: {
          category?: string | null
          confidential_notes?: string | null
          created_at?: string
          description?: string
          hearing_date?: string | null
          id?: string
          incident_date?: string
          outcome?: string | null
          reported_by?: string | null
          sanction?: string | null
          severity?: string | null
          status?: string
          student_id?: string
          updated_at?: string
        }
        Relationships: [
          {
            foreignKeyName: "discipline_cases_student_id_fkey"
            columns: ["student_id"]
            isOneToOne: false
            referencedRelation: "students"
            referencedColumns: ["id"]
          },
        ]
      }
      email_send_log: {
        Row: {
          created_at: string
          error_message: string | null
          id: string
          message_id: string | null
          metadata: Json | null
          recipient_email: string
          status: string
          template_name: string
        }
        Insert: {
          created_at?: string
          error_message?: string | null
          id?: string
          message_id?: string | null
          metadata?: Json | null
          recipient_email: string
          status: string
          template_name: string
        }
        Update: {
          created_at?: string
          error_message?: string | null
          id?: string
          message_id?: string | null
          metadata?: Json | null
          recipient_email?: string
          status?: string
          template_name?: string
        }
        Relationships: []
      }
      email_send_state: {
        Row: {
          auth_email_ttl_minutes: number
          batch_size: number
          id: number
          retry_after_until: string | null
          send_delay_ms: number
          transactional_email_ttl_minutes: number
          updated_at: string
        }
        Insert: {
          auth_email_ttl_minutes?: number
          batch_size?: number
          id?: number
          retry_after_until?: string | null
          send_delay_ms?: number
          transactional_email_ttl_minutes?: number
          updated_at?: string
        }
        Update: {
          auth_email_ttl_minutes?: number
          batch_size?: number
          id?: number
          retry_after_until?: string | null
          send_delay_ms?: number
          transactional_email_ttl_minutes?: number
          updated_at?: string
        }
        Relationships: []
      }
      email_unsubscribe_tokens: {
        Row: {
          created_at: string
          email: string
          id: string
          token: string
          used_at: string | null
        }
        Insert: {
          created_at?: string
          email: string
          id?: string
          token: string
          used_at?: string | null
        }
        Update: {
          created_at?: string
          email?: string
          id?: string
          token?: string
          used_at?: string | null
        }
        Relationships: []
      }
      exam_batch_verifications: {
        Row: {
          assigned_by: string
          assigned_to: string
          auto_granted_access: boolean
          batch_id: string
          created_at: string
          id: string
          notes: string | null
          status: string
          updated_at: string
        }
        Insert: {
          assigned_by: string
          assigned_to: string
          auto_granted_access?: boolean
          batch_id: string
          created_at?: string
          id?: string
          notes?: string | null
          status?: string
          updated_at?: string
        }
        Update: {
          assigned_by?: string
          assigned_to?: string
          auto_granted_access?: boolean
          batch_id?: string
          created_at?: string
          id?: string
          notes?: string | null
          status?: string
          updated_at?: string
        }
        Relationships: [
          {
            foreignKeyName: "exam_batch_verifications_batch_id_fkey"
            columns: ["batch_id"]
            isOneToOne: false
            referencedRelation: "exam_batches"
            referencedColumns: ["id"]
          },
        ]
      }
      exam_batches: {
        Row: {
          academic_year: string
          approved_at: string | null
          approved_by: string | null
          class: string
          created_at: string
          exam_type: string
          id: string
          rejection_notes: string | null
          semester: string
          status: string
          total_errors: number | null
          total_students: number | null
          updated_at: string
          uploaded_by: string
        }
        Insert: {
          academic_year: string
          approved_at?: string | null
          approved_by?: string | null
          class: string
          created_at?: string
          exam_type: string
          id?: string
          rejection_notes?: string | null
          semester: string
          status?: string
          total_errors?: number | null
          total_students?: number | null
          updated_at?: string
          uploaded_by: string
        }
        Update: {
          academic_year?: string
          approved_at?: string | null
          approved_by?: string | null
          class?: string
          created_at?: string
          exam_type?: string
          id?: string
          rejection_notes?: string | null
          semester?: string
          status?: string
          total_errors?: number | null
          total_students?: number | null
          updated_at?: string
          uploaded_by?: string
        }
        Relationships: []
      }
      exam_result_attempts: {
        Row: {
          attempt_number: number
          created_at: string
          exam_type: string | null
          id: string
          new_grade: string | null
          new_marks: number | null
          new_remark: string | null
          previous_grade: string | null
          previous_marks: number | null
          previous_remark: string | null
          result_id: string
          uploaded_by: string | null
        }
        Insert: {
          attempt_number?: number
          created_at?: string
          exam_type?: string | null
          id?: string
          new_grade?: string | null
          new_marks?: number | null
          new_remark?: string | null
          previous_grade?: string | null
          previous_marks?: number | null
          previous_remark?: string | null
          result_id: string
          uploaded_by?: string | null
        }
        Update: {
          attempt_number?: number
          created_at?: string
          exam_type?: string | null
          id?: string
          new_grade?: string | null
          new_marks?: number | null
          new_remark?: string | null
          previous_grade?: string | null
          previous_marks?: number | null
          previous_remark?: string | null
          result_id?: string
          uploaded_by?: string | null
        }
        Relationships: [
          {
            foreignKeyName: "exam_result_attempts_result_id_fkey"
            columns: ["result_id"]
            isOneToOne: false
            referencedRelation: "exam_results"
            referencedColumns: ["id"]
          },
        ]
      }
      exam_results: {
        Row: {
          academic_year: string
          approved_by: string | null
          batch_id: string | null
          course_id: string
          created_at: string
          entered_by: string | null
          exam_type: string | null
          form_iv_index: string | null
          gpa: string | null
          grade: string | null
          id: string
          marks: number | null
          overall_remark: string | null
          pre_registration_id: string | null
          remark: string | null
          semester: string
          status: string
          student_id: string | null
          student_source: string
          updated_at: string
        }
        Insert: {
          academic_year: string
          approved_by?: string | null
          batch_id?: string | null
          course_id: string
          created_at?: string
          entered_by?: string | null
          exam_type?: string | null
          form_iv_index?: string | null
          gpa?: string | null
          grade?: string | null
          id?: string
          marks?: number | null
          overall_remark?: string | null
          pre_registration_id?: string | null
          remark?: string | null
          semester: string
          status?: string
          student_id?: string | null
          student_source?: string
          updated_at?: string
        }
        Update: {
          academic_year?: string
          approved_by?: string | null
          batch_id?: string | null
          course_id?: string
          created_at?: string
          entered_by?: string | null
          exam_type?: string | null
          form_iv_index?: string | null
          gpa?: string | null
          grade?: string | null
          id?: string
          marks?: number | null
          overall_remark?: string | null
          pre_registration_id?: string | null
          remark?: string | null
          semester?: string
          status?: string
          student_id?: string | null
          student_source?: string
          updated_at?: string
        }
        Relationships: [
          {
            foreignKeyName: "exam_results_batch_id_fkey"
            columns: ["batch_id"]
            isOneToOne: false
            referencedRelation: "exam_batches"
            referencedColumns: ["id"]
          },
          {
            foreignKeyName: "exam_results_course_id_fkey"
            columns: ["course_id"]
            isOneToOne: false
            referencedRelation: "courses"
            referencedColumns: ["id"]
          },
          {
            foreignKeyName: "exam_results_pre_registration_id_fkey"
            columns: ["pre_registration_id"]
            isOneToOne: false
            referencedRelation: "student_pre_registrations"
            referencedColumns: ["id"]
          },
          {
            foreignKeyName: "exam_results_pre_registration_id_fkey"
            columns: ["pre_registration_id"]
            isOneToOne: false
            referencedRelation: "student_pre_registrations_exam_safe"
            referencedColumns: ["id"]
          },
          {
            foreignKeyName: "exam_results_student_id_fkey"
            columns: ["student_id"]
            isOneToOne: false
            referencedRelation: "students"
            referencedColumns: ["id"]
          },
        ]
      }
      fee_categories: {
        Row: {
          academic_year: string
          classes: string[]
          created_at: string
          created_by: string | null
          id: string
          name: string
          notes: string | null
          student_type: string
          updated_at: string
        }
        Insert: {
          academic_year: string
          classes?: string[]
          created_at?: string
          created_by?: string | null
          id?: string
          name: string
          notes?: string | null
          student_type?: string
          updated_at?: string
        }
        Update: {
          academic_year?: string
          classes?: string[]
          created_at?: string
          created_by?: string | null
          id?: string
          name?: string
          notes?: string | null
          student_type?: string
          updated_at?: string
        }
        Relationships: []
      }
      fee_cost_types: {
        Row: {
          code: string
          created_at: string
          id: string
          is_default_continuing: boolean
          is_default_first_year: boolean
          name: string
          sort_order: number
        }
        Insert: {
          code: string
          created_at?: string
          id?: string
          is_default_continuing?: boolean
          is_default_first_year?: boolean
          name: string
          sort_order?: number
        }
        Update: {
          code?: string
          created_at?: string
          id?: string
          is_default_continuing?: boolean
          is_default_first_year?: boolean
          name?: string
          sort_order?: number
        }
        Relationships: []
      }
      fee_installments: {
        Row: {
          amount: number
          category_id: string
          created_at: string
          due_date: string
          end_date: string | null
          id: string
          installment_no: number
          kind: string
          label: string | null
          semester: string
          start_date: string | null
        }
        Insert: {
          amount?: number
          category_id: string
          created_at?: string
          due_date: string
          end_date?: string | null
          id?: string
          installment_no: number
          kind?: string
          label?: string | null
          semester: string
          start_date?: string | null
        }
        Update: {
          amount?: number
          category_id?: string
          created_at?: string
          due_date?: string
          end_date?: string | null
          id?: string
          installment_no?: number
          kind?: string
          label?: string | null
          semester?: string
          start_date?: string | null
        }
        Relationships: [
          {
            foreignKeyName: "fee_installments_category_id_fkey"
            columns: ["category_id"]
            isOneToOne: false
            referencedRelation: "fee_categories"
            referencedColumns: ["id"]
          },
        ]
      }
      fee_structure_items: {
        Row: {
          academic_year: string
          amount: number
          category_id: string
          cost_type_id: string
          created_at: string
          id: string
          semester: string
        }
        Insert: {
          academic_year: string
          amount?: number
          category_id: string
          cost_type_id: string
          created_at?: string
          id?: string
          semester: string
        }
        Update: {
          academic_year?: string
          amount?: number
          category_id?: string
          cost_type_id?: string
          created_at?: string
          id?: string
          semester?: string
        }
        Relationships: [
          {
            foreignKeyName: "fee_structure_items_category_id_fkey"
            columns: ["category_id"]
            isOneToOne: false
            referencedRelation: "fee_categories"
            referencedColumns: ["id"]
          },
          {
            foreignKeyName: "fee_structure_items_cost_type_id_fkey"
            columns: ["cost_type_id"]
            isOneToOne: false
            referencedRelation: "fee_cost_types"
            referencedColumns: ["id"]
          },
        ]
      }
      fee_structures: {
        Row: {
          academic_year: string
          amount: number
          created_at: string
          description: string | null
          id: string
          program: string
          semester: string
        }
        Insert: {
          academic_year: string
          amount: number
          created_at?: string
          description?: string | null
          id?: string
          program: string
          semester: string
        }
        Update: {
          academic_year?: string
          amount?: number
          created_at?: string
          description?: string | null
          id?: string
          program?: string
          semester?: string
        }
        Relationships: []
      }
      field_group_dates: {
        Row: {
          created_at: string
          group_id: string
          id: string
          placement_date: string
        }
        Insert: {
          created_at?: string
          group_id: string
          id?: string
          placement_date: string
        }
        Update: {
          created_at?: string
          group_id?: string
          id?: string
          placement_date?: string
        }
        Relationships: [
          {
            foreignKeyName: "field_group_dates_group_id_fkey"
            columns: ["group_id"]
            isOneToOne: false
            referencedRelation: "field_groups"
            referencedColumns: ["id"]
          },
        ]
      }
      field_group_members: {
        Row: {
          created_at: string
          group_id: string
          id: string
          student_id: string
        }
        Insert: {
          created_at?: string
          group_id: string
          id?: string
          student_id: string
        }
        Update: {
          created_at?: string
          group_id?: string
          id?: string
          student_id?: string
        }
        Relationships: [
          {
            foreignKeyName: "field_group_members_group_id_fkey"
            columns: ["group_id"]
            isOneToOne: false
            referencedRelation: "field_groups"
            referencedColumns: ["id"]
          },
          {
            foreignKeyName: "field_group_members_student_id_fkey"
            columns: ["student_id"]
            isOneToOne: false
            referencedRelation: "students"
            referencedColumns: ["id"]
          },
        ]
      }
      field_groups: {
        Row: {
          approval_status: string
          approved_at: string | null
          approved_by: string | null
          class: string
          created_at: string
          created_by: string
          id: string
          lat_override: number | null
          leader_student_id: string | null
          lng_override: number | null
          max_radius_m: number | null
          name: string
          radius_override_m: number | null
          scan_end_time: string | null
          scan_start_time: string | null
          semester: string
          site_id: string | null
          site_name_override: string | null
          updated_at: string
        }
        Insert: {
          approval_status?: string
          approved_at?: string | null
          approved_by?: string | null
          class: string
          created_at?: string
          created_by: string
          id?: string
          lat_override?: number | null
          leader_student_id?: string | null
          lng_override?: number | null
          max_radius_m?: number | null
          name: string
          radius_override_m?: number | null
          scan_end_time?: string | null
          scan_start_time?: string | null
          semester: string
          site_id?: string | null
          site_name_override?: string | null
          updated_at?: string
        }
        Update: {
          approval_status?: string
          approved_at?: string | null
          approved_by?: string | null
          class?: string
          created_at?: string
          created_by?: string
          id?: string
          lat_override?: number | null
          leader_student_id?: string | null
          lng_override?: number | null
          max_radius_m?: number | null
          name?: string
          radius_override_m?: number | null
          scan_end_time?: string | null
          scan_start_time?: string | null
          semester?: string
          site_id?: string | null
          site_name_override?: string | null
          updated_at?: string
        }
        Relationships: [
          {
            foreignKeyName: "field_groups_leader_student_id_fkey"
            columns: ["leader_student_id"]
            isOneToOne: false
            referencedRelation: "students"
            referencedColumns: ["id"]
          },
          {
            foreignKeyName: "field_groups_site_id_fkey"
            columns: ["site_id"]
            isOneToOne: false
            referencedRelation: "field_sites"
            referencedColumns: ["id"]
          },
        ]
      }
      field_sites: {
        Row: {
          active: boolean
          address: string | null
          created_at: string
          created_by: string
          id: string
          latitude: number
          longitude: number
          name: string
          radius_m: number
          updated_at: string
        }
        Insert: {
          active?: boolean
          address?: string | null
          created_at?: string
          created_by: string
          id?: string
          latitude: number
          longitude: number
          name: string
          radius_m?: number
          updated_at?: string
        }
        Update: {
          active?: boolean
          address?: string | null
          created_at?: string
          created_by?: string
          id?: string
          latitude?: number
          longitude?: number
          name?: string
          radius_m?: number
          updated_at?: string
        }
        Relationships: []
      }
      invitations: {
        Row: {
          created_at: string
          email: string
          expires_at: string
          full_name: string
          id: string
          invited_by: string
          phone: string | null
          role: Database["public"]["Enums"]["app_role"]
          status: string
          token: string
        }
        Insert: {
          created_at?: string
          email: string
          expires_at?: string
          full_name: string
          id?: string
          invited_by: string
          phone?: string | null
          role: Database["public"]["Enums"]["app_role"]
          status?: string
          token?: string
        }
        Update: {
          created_at?: string
          email?: string
          expires_at?: string
          full_name?: string
          id?: string
          invited_by?: string
          phone?: string | null
          role?: Database["public"]["Enums"]["app_role"]
          status?: string
          token?: string
        }
        Relationships: []
      }
      invoices: {
        Row: {
          advance_payment: number
          amount: number
          control_number: string | null
          cost_type_id: string | null
          created_at: string
          description: string | null
          discount_amount: number
          due_amount: number | null
          due_date: string | null
          id: string
          kind: string
          status: string
          student_id: string
          updated_at: string
        }
        Insert: {
          advance_payment?: number
          amount: number
          control_number?: string | null
          cost_type_id?: string | null
          created_at?: string
          description?: string | null
          discount_amount?: number
          due_amount?: number | null
          due_date?: string | null
          id?: string
          kind?: string
          status?: string
          student_id: string
          updated_at?: string
        }
        Update: {
          advance_payment?: number
          amount?: number
          control_number?: string | null
          cost_type_id?: string | null
          created_at?: string
          description?: string | null
          discount_amount?: number
          due_amount?: number | null
          due_date?: string | null
          id?: string
          kind?: string
          status?: string
          student_id?: string
          updated_at?: string
        }
        Relationships: [
          {
            foreignKeyName: "invoices_cost_type_id_fkey"
            columns: ["cost_type_id"]
            isOneToOne: false
            referencedRelation: "fee_cost_types"
            referencedColumns: ["id"]
          },
          {
            foreignKeyName: "invoices_student_id_fkey"
            columns: ["student_id"]
            isOneToOne: false
            referencedRelation: "students"
            referencedColumns: ["id"]
          },
        ]
      }
      meeting_agenda_items: {
        Row: {
          created_at: string
          deadline: string | null
          discussion_notes: string | null
          id: string
          item_type: string
          meeting_id: string
          resolution: string | null
          resources_required: string | null
          responsible_person: string | null
          sort_order: number
          task_description: string | null
          title: string
          updated_at: string
        }
        Insert: {
          created_at?: string
          deadline?: string | null
          discussion_notes?: string | null
          id?: string
          item_type?: string
          meeting_id: string
          resolution?: string | null
          resources_required?: string | null
          responsible_person?: string | null
          sort_order?: number
          task_description?: string | null
          title: string
          updated_at?: string
        }
        Update: {
          created_at?: string
          deadline?: string | null
          discussion_notes?: string | null
          id?: string
          item_type?: string
          meeting_id?: string
          resolution?: string | null
          resources_required?: string | null
          responsible_person?: string | null
          sort_order?: number
          task_description?: string | null
          title?: string
          updated_at?: string
        }
        Relationships: [
          {
            foreignKeyName: "meeting_agenda_items_meeting_id_fkey"
            columns: ["meeting_id"]
            isOneToOne: false
            referencedRelation: "meetings"
            referencedColumns: ["id"]
          },
        ]
      }
      meeting_events: {
        Row: {
          actor_user_id: string | null
          created_at: string
          event_type: string
          id: string
          meeting_id: string
          payload_json: Json | null
        }
        Insert: {
          actor_user_id?: string | null
          created_at?: string
          event_type: string
          id?: string
          meeting_id: string
          payload_json?: Json | null
        }
        Update: {
          actor_user_id?: string | null
          created_at?: string
          event_type?: string
          id?: string
          meeting_id?: string
          payload_json?: Json | null
        }
        Relationships: [
          {
            foreignKeyName: "meeting_events_meeting_id_fkey"
            columns: ["meeting_id"]
            isOneToOne: false
            referencedRelation: "meetings"
            referencedColumns: ["id"]
          },
        ]
      }
      meeting_minutes: {
        Row: {
          content: string
          created_at: string
          finalized: boolean
          finalized_at: string | null
          finalized_by: string | null
          id: string
          meeting_id: string
          updated_at: string
        }
        Insert: {
          content?: string
          created_at?: string
          finalized?: boolean
          finalized_at?: string | null
          finalized_by?: string | null
          id?: string
          meeting_id: string
          updated_at?: string
        }
        Update: {
          content?: string
          created_at?: string
          finalized?: boolean
          finalized_at?: string | null
          finalized_by?: string | null
          id?: string
          meeting_id?: string
          updated_at?: string
        }
        Relationships: [
          {
            foreignKeyName: "meeting_minutes_meeting_id_fkey"
            columns: ["meeting_id"]
            isOneToOne: true
            referencedRelation: "meetings"
            referencedColumns: ["id"]
          },
        ]
      }
      meeting_participants: {
        Row: {
          created_at: string
          display_name: string | null
          id: string
          joined_at: string | null
          left_at: string | null
          meeting_id: string
          role: string | null
          signature_url: string | null
          user_id: string
        }
        Insert: {
          created_at?: string
          display_name?: string | null
          id?: string
          joined_at?: string | null
          left_at?: string | null
          meeting_id: string
          role?: string | null
          signature_url?: string | null
          user_id: string
        }
        Update: {
          created_at?: string
          display_name?: string | null
          id?: string
          joined_at?: string | null
          left_at?: string | null
          meeting_id?: string
          role?: string | null
          signature_url?: string | null
          user_id?: string
        }
        Relationships: [
          {
            foreignKeyName: "meeting_participants_meeting_id_fkey"
            columns: ["meeting_id"]
            isOneToOne: false
            referencedRelation: "meetings"
            referencedColumns: ["id"]
          },
        ]
      }
      meeting_resolution_followups: {
        Row: {
          created_at: string
          current_status: string | null
          discussion_note: string | null
          follow_up_action: string | null
          id: string
          meeting_id: string
          original_resolution: string | null
          source_agenda_item_id: string | null
          source_meeting_id: string | null
          updated_at: string
        }
        Insert: {
          created_at?: string
          current_status?: string | null
          discussion_note?: string | null
          follow_up_action?: string | null
          id?: string
          meeting_id: string
          original_resolution?: string | null
          source_agenda_item_id?: string | null
          source_meeting_id?: string | null
          updated_at?: string
        }
        Update: {
          created_at?: string
          current_status?: string | null
          discussion_note?: string | null
          follow_up_action?: string | null
          id?: string
          meeting_id?: string
          original_resolution?: string | null
          source_agenda_item_id?: string | null
          source_meeting_id?: string | null
          updated_at?: string
        }
        Relationships: [
          {
            foreignKeyName: "meeting_resolution_followups_meeting_id_fkey"
            columns: ["meeting_id"]
            isOneToOne: false
            referencedRelation: "meetings"
            referencedColumns: ["id"]
          },
          {
            foreignKeyName: "meeting_resolution_followups_source_agenda_item_id_fkey"
            columns: ["source_agenda_item_id"]
            isOneToOne: false
            referencedRelation: "meeting_agenda_items"
            referencedColumns: ["id"]
          },
          {
            foreignKeyName: "meeting_resolution_followups_source_meeting_id_fkey"
            columns: ["source_meeting_id"]
            isOneToOne: false
            referencedRelation: "meetings"
            referencedColumns: ["id"]
          },
        ]
      }
      meetings: {
        Row: {
          chair_user_id: string | null
          created_at: string
          created_by: string
          department: string | null
          ended_at: string | null
          id: string
          location: string | null
          meeting_type: string | null
          previous_meeting_id: string | null
          recording_enabled: boolean | null
          recording_url: string | null
          room_name: string
          scheduled_at: string | null
          secretary_user_id: string | null
          started_at: string | null
          status: string
          summary: string | null
          title: string
          transcript: string | null
          transcription_enabled: boolean | null
          updated_at: string
        }
        Insert: {
          chair_user_id?: string | null
          created_at?: string
          created_by: string
          department?: string | null
          ended_at?: string | null
          id?: string
          location?: string | null
          meeting_type?: string | null
          previous_meeting_id?: string | null
          recording_enabled?: boolean | null
          recording_url?: string | null
          room_name: string
          scheduled_at?: string | null
          secretary_user_id?: string | null
          started_at?: string | null
          status?: string
          summary?: string | null
          title: string
          transcript?: string | null
          transcription_enabled?: boolean | null
          updated_at?: string
        }
        Update: {
          chair_user_id?: string | null
          created_at?: string
          created_by?: string
          department?: string | null
          ended_at?: string | null
          id?: string
          location?: string | null
          meeting_type?: string | null
          previous_meeting_id?: string | null
          recording_enabled?: boolean | null
          recording_url?: string | null
          room_name?: string
          scheduled_at?: string | null
          secretary_user_id?: string | null
          started_at?: string | null
          status?: string
          summary?: string | null
          title?: string
          transcript?: string | null
          transcription_enabled?: boolean | null
          updated_at?: string
        }
        Relationships: [
          {
            foreignKeyName: "meetings_previous_meeting_id_fkey"
            columns: ["previous_meeting_id"]
            isOneToOne: false
            referencedRelation: "meetings"
            referencedColumns: ["id"]
          },
        ]
      }
      message_templates: {
        Row: {
          body: string
          category: string
          created_at: string
          created_by: string
          id: string
          title: string
          updated_at: string
        }
        Insert: {
          body: string
          category?: string
          created_at?: string
          created_by: string
          id?: string
          title: string
          updated_at?: string
        }
        Update: {
          body?: string
          category?: string
          created_at?: string
          created_by?: string
          id?: string
          title?: string
          updated_at?: string
        }
        Relationships: []
      }
      module_sessions: {
        Row: {
          active: boolean
          class: string
          created_at: string
          created_by: string | null
          id: string
          module_code: string
          module_name: string | null
          semester: string
          session_kind: string
          session_name: string
          session_no: number
          updated_at: string
        }
        Insert: {
          active?: boolean
          class: string
          created_at?: string
          created_by?: string | null
          id?: string
          module_code: string
          module_name?: string | null
          semester: string
          session_kind?: string
          session_name: string
          session_no: number
          updated_at?: string
        }
        Update: {
          active?: boolean
          class?: string
          created_at?: string
          created_by?: string | null
          id?: string
          module_code?: string
          module_name?: string | null
          semester?: string
          session_kind?: string
          session_name?: string
          session_no?: number
          updated_at?: string
        }
        Relationships: []
      }
      module_visibility: {
        Row: {
          created_at: string
          id: string
          module_key: string
          role: Database["public"]["Enums"]["app_role"]
          updated_at: string
          visible: boolean
        }
        Insert: {
          created_at?: string
          id?: string
          module_key: string
          role: Database["public"]["Enums"]["app_role"]
          updated_at?: string
          visible?: boolean
        }
        Update: {
          created_at?: string
          id?: string
          module_key?: string
          role?: Database["public"]["Enums"]["app_role"]
          updated_at?: string
          visible?: boolean
        }
        Relationships: []
      }
      modules: {
        Row: {
          class: string
          created_at: string
          credits: number | null
          id: string
          module_code: string
          module_name: string
          semester: string
          updated_at: string
        }
        Insert: {
          class: string
          created_at?: string
          credits?: number | null
          id?: string
          module_code: string
          module_name: string
          semester?: string
          updated_at?: string
        }
        Update: {
          class?: string
          created_at?: string
          credits?: number | null
          id?: string
          module_code?: string
          module_name?: string
          semester?: string
          updated_at?: string
        }
        Relationships: []
      }
      notifications: {
        Row: {
          created_at: string
          id: string
          link: string | null
          message: string | null
          read: boolean
          title: string
          type: string
          user_id: string
        }
        Insert: {
          created_at?: string
          id?: string
          link?: string | null
          message?: string | null
          read?: boolean
          title: string
          type?: string
          user_id: string
        }
        Update: {
          created_at?: string
          id?: string
          link?: string | null
          message?: string | null
          read?: boolean
          title?: string
          type?: string
          user_id?: string
        }
        Relationships: []
      }
      payments: {
        Row: {
          amount: number
          created_at: string
          id: string
          invoice_id: string
          payment_date: string | null
          payment_method: string | null
          reference_number: string | null
          student_id: string
        }
        Insert: {
          amount: number
          created_at?: string
          id?: string
          invoice_id: string
          payment_date?: string | null
          payment_method?: string | null
          reference_number?: string | null
          student_id: string
        }
        Update: {
          amount?: number
          created_at?: string
          id?: string
          invoice_id?: string
          payment_date?: string | null
          payment_method?: string | null
          reference_number?: string | null
          student_id?: string
        }
        Relationships: [
          {
            foreignKeyName: "payments_invoice_id_fkey"
            columns: ["invoice_id"]
            isOneToOne: false
            referencedRelation: "invoices"
            referencedColumns: ["id"]
          },
          {
            foreignKeyName: "payments_student_id_fkey"
            columns: ["student_id"]
            isOneToOne: false
            referencedRelation: "students"
            referencedColumns: ["id"]
          },
        ]
      }
      private_documents: {
        Row: {
          created_at: string
          description: string | null
          file_path: string
          file_size: number | null
          id: string
          mime_type: string | null
          owner_id: string
          title: string
          updated_at: string
        }
        Insert: {
          created_at?: string
          description?: string | null
          file_path: string
          file_size?: number | null
          id?: string
          mime_type?: string | null
          owner_id: string
          title: string
          updated_at?: string
        }
        Update: {
          created_at?: string
          description?: string | null
          file_path?: string
          file_size?: number | null
          id?: string
          mime_type?: string | null
          owner_id?: string
          title?: string
          updated_at?: string
        }
        Relationships: []
      }
      profiles: {
        Row: {
          avatar_url: string | null
          created_at: string
          full_name: string | null
          id: string
          phone: string | null
          salutation: string | null
          status: string
          updated_at: string
          user_id: string
        }
        Insert: {
          avatar_url?: string | null
          created_at?: string
          full_name?: string | null
          id?: string
          phone?: string | null
          salutation?: string | null
          status?: string
          updated_at?: string
          user_id: string
        }
        Update: {
          avatar_url?: string | null
          created_at?: string
          full_name?: string | null
          id?: string
          phone?: string | null
          salutation?: string | null
          status?: string
          updated_at?: string
          user_id?: string
        }
        Relationships: []
      }
      qr_bypass_roles: {
        Row: {
          created_at: string
          created_by: string | null
          id: string
          qr_type: string
          role: Database["public"]["Enums"]["app_role"]
        }
        Insert: {
          created_at?: string
          created_by?: string | null
          id?: string
          qr_type: string
          role: Database["public"]["Enums"]["app_role"]
        }
        Update: {
          created_at?: string
          created_by?: string | null
          id?: string
          qr_type?: string
          role?: Database["public"]["Enums"]["app_role"]
        }
        Relationships: []
      }
      qr_token_uses: {
        Row: {
          id: string
          qr_type: string
          scanned_by: string | null
          scanner_context: string | null
          student_id: string | null
          token_hash: string
          used_at: string
        }
        Insert: {
          id?: string
          qr_type: string
          scanned_by?: string | null
          scanner_context?: string | null
          student_id?: string | null
          token_hash: string
          used_at?: string
        }
        Update: {
          id?: string
          qr_type?: string
          scanned_by?: string | null
          scanner_context?: string | null
          student_id?: string | null
          token_hash?: string
          used_at?: string
        }
        Relationships: [
          {
            foreignKeyName: "qr_token_uses_student_id_fkey"
            columns: ["student_id"]
            isOneToOne: false
            referencedRelation: "students"
            referencedColumns: ["id"]
          },
        ]
      }
      qr_visibility_rules: {
        Row: {
          created_at: string
          created_by: string
          date_from: string | null
          date_to: string | null
          day_of_week: string[]
          geo_enabled: boolean
          geo_latitude: number | null
          geo_longitude: number | null
          geo_radius_meters: number | null
          id: string
          is_active: boolean
          qr_type: string
          target_classes: string[]
          time_windows: Json
          updated_at: string
          visible_to_roles: string[]
        }
        Insert: {
          created_at?: string
          created_by: string
          date_from?: string | null
          date_to?: string | null
          day_of_week?: string[]
          geo_enabled?: boolean
          geo_latitude?: number | null
          geo_longitude?: number | null
          geo_radius_meters?: number | null
          id?: string
          is_active?: boolean
          qr_type: string
          target_classes?: string[]
          time_windows?: Json
          updated_at?: string
          visible_to_roles?: string[]
        }
        Update: {
          created_at?: string
          created_by?: string
          date_from?: string | null
          date_to?: string | null
          day_of_week?: string[]
          geo_enabled?: boolean
          geo_latitude?: number | null
          geo_longitude?: number | null
          geo_radius_meters?: number | null
          id?: string
          is_active?: boolean
          qr_type?: string
          target_classes?: string[]
          time_windows?: Json
          updated_at?: string
          visible_to_roles?: string[]
        }
        Relationships: []
      }
      registrations: {
        Row: {
          academic_year: string
          course_id: string
          created_at: string
          id: string
          semester: string
          status: string
          student_id: string
        }
        Insert: {
          academic_year: string
          course_id: string
          created_at?: string
          id?: string
          semester: string
          status?: string
          student_id: string
        }
        Update: {
          academic_year?: string
          course_id?: string
          created_at?: string
          id?: string
          semester?: string
          status?: string
          student_id?: string
        }
        Relationships: [
          {
            foreignKeyName: "registrations_course_id_fkey"
            columns: ["course_id"]
            isOneToOne: false
            referencedRelation: "courses"
            referencedColumns: ["id"]
          },
          {
            foreignKeyName: "registrations_student_id_fkey"
            columns: ["student_id"]
            isOneToOne: false
            referencedRelation: "students"
            referencedColumns: ["id"]
          },
        ]
      }
      remedial_session_requests: {
        Row: {
          closed_at: string | null
          created_at: string
          decided_at: string | null
          decided_by: string | null
          decision_comment: string | null
          id: string
          opened_at: string | null
          reason: string
          remedial_session_id: string | null
          requested_by: string
          session_id: string
          status: string
          updated_at: string
        }
        Insert: {
          closed_at?: string | null
          created_at?: string
          decided_at?: string | null
          decided_by?: string | null
          decision_comment?: string | null
          id?: string
          opened_at?: string | null
          reason: string
          remedial_session_id?: string | null
          requested_by: string
          session_id: string
          status?: string
          updated_at?: string
        }
        Update: {
          closed_at?: string | null
          created_at?: string
          decided_at?: string | null
          decided_by?: string | null
          decision_comment?: string | null
          id?: string
          opened_at?: string | null
          reason?: string
          remedial_session_id?: string | null
          requested_by?: string
          session_id?: string
          status?: string
          updated_at?: string
        }
        Relationships: [
          {
            foreignKeyName: "remedial_session_requests_remedial_session_id_fkey"
            columns: ["remedial_session_id"]
            isOneToOne: false
            referencedRelation: "attendance_sessions"
            referencedColumns: ["id"]
          },
          {
            foreignKeyName: "remedial_session_requests_session_id_fkey"
            columns: ["session_id"]
            isOneToOne: false
            referencedRelation: "attendance_sessions"
            referencedColumns: ["id"]
          },
        ]
      }
      result_visibility_rules: {
        Row: {
          batch_id: string | null
          created_at: string
          ends_at: string | null
          exam_type: string | null
          id: string
          reason: string | null
          scope_type: string
          scope_value: string
          set_by: string
          starts_at: string | null
          updated_at: string
          visibility_status: string
        }
        Insert: {
          batch_id?: string | null
          created_at?: string
          ends_at?: string | null
          exam_type?: string | null
          id?: string
          reason?: string | null
          scope_type: string
          scope_value: string
          set_by: string
          starts_at?: string | null
          updated_at?: string
          visibility_status?: string
        }
        Update: {
          batch_id?: string | null
          created_at?: string
          ends_at?: string | null
          exam_type?: string | null
          id?: string
          reason?: string | null
          scope_type?: string
          scope_value?: string
          set_by?: string
          starts_at?: string | null
          updated_at?: string
          visibility_status?: string
        }
        Relationships: [
          {
            foreignKeyName: "result_visibility_rules_batch_id_fkey"
            columns: ["batch_id"]
            isOneToOne: false
            referencedRelation: "exam_batches"
            referencedColumns: ["id"]
          },
        ]
      }
      role_permissions: {
        Row: {
          can_create: boolean
          can_delete: boolean
          can_read: boolean
          can_update: boolean
          created_at: string
          id: string
          module_key: string
          role: Database["public"]["Enums"]["app_role"]
          updated_at: string
        }
        Insert: {
          can_create?: boolean
          can_delete?: boolean
          can_read?: boolean
          can_update?: boolean
          created_at?: string
          id?: string
          module_key: string
          role: Database["public"]["Enums"]["app_role"]
          updated_at?: string
        }
        Update: {
          can_create?: boolean
          can_delete?: boolean
          can_read?: boolean
          can_update?: boolean
          created_at?: string
          id?: string
          module_key?: string
          role?: Database["public"]["Enums"]["app_role"]
          updated_at?: string
        }
        Relationships: []
      }
      rotation_attendance_alerts: {
        Row: {
          class: string
          created_at: string
          field_group_id: string
          id: string
          present_days: number
          rate_pct: number
          scheduled_days: number
          semester: string
          student_id: string
          threshold_pct: number
        }
        Insert: {
          class: string
          created_at?: string
          field_group_id: string
          id?: string
          present_days: number
          rate_pct: number
          scheduled_days: number
          semester: string
          student_id: string
          threshold_pct: number
        }
        Update: {
          class?: string
          created_at?: string
          field_group_id?: string
          id?: string
          present_days?: number
          rate_pct?: number
          scheduled_days?: number
          semester?: string
          student_id?: string
          threshold_pct?: number
        }
        Relationships: [
          {
            foreignKeyName: "rotation_attendance_alerts_field_group_id_fkey"
            columns: ["field_group_id"]
            isOneToOne: false
            referencedRelation: "field_groups"
            referencedColumns: ["id"]
          },
          {
            foreignKeyName: "rotation_attendance_alerts_student_id_fkey"
            columns: ["student_id"]
            isOneToOne: false
            referencedRelation: "students"
            referencedColumns: ["id"]
          },
        ]
      }
      rotation_attendance_overrides: {
        Row: {
          attendance_date: string
          attendance_record_id: string | null
          created_at: string
          field_group_id: string
          id: string
          justification: string
          justification_file_url: string | null
          lat: number | null
          lng: number | null
          performed_by: string
          radius_m: number | null
          site_id: string | null
          site_name_override: string | null
          status: string
          student_id: string
        }
        Insert: {
          attendance_date: string
          attendance_record_id?: string | null
          created_at?: string
          field_group_id: string
          id?: string
          justification: string
          justification_file_url?: string | null
          lat?: number | null
          lng?: number | null
          performed_by: string
          radius_m?: number | null
          site_id?: string | null
          site_name_override?: string | null
          status?: string
          student_id: string
        }
        Update: {
          attendance_date?: string
          attendance_record_id?: string | null
          created_at?: string
          field_group_id?: string
          id?: string
          justification?: string
          justification_file_url?: string | null
          lat?: number | null
          lng?: number | null
          performed_by?: string
          radius_m?: number | null
          site_id?: string | null
          site_name_override?: string | null
          status?: string
          student_id?: string
        }
        Relationships: [
          {
            foreignKeyName: "rotation_attendance_overrides_attendance_record_id_fkey"
            columns: ["attendance_record_id"]
            isOneToOne: false
            referencedRelation: "attendance_records"
            referencedColumns: ["id"]
          },
          {
            foreignKeyName: "rotation_attendance_overrides_field_group_id_fkey"
            columns: ["field_group_id"]
            isOneToOne: false
            referencedRelation: "field_groups"
            referencedColumns: ["id"]
          },
          {
            foreignKeyName: "rotation_attendance_overrides_site_id_fkey"
            columns: ["site_id"]
            isOneToOne: false
            referencedRelation: "field_sites"
            referencedColumns: ["id"]
          },
          {
            foreignKeyName: "rotation_attendance_overrides_student_id_fkey"
            columns: ["student_id"]
            isOneToOne: false
            referencedRelation: "students"
            referencedColumns: ["id"]
          },
        ]
      }
      rotation_backdate_grants: {
        Row: {
          attendance_date: string
          consumed_at: string | null
          expires_at: string
          field_group_id: string
          granted_at: string
          granted_by: string
          granted_to: string
          id: string
          reason: string
          revoked_at: string | null
        }
        Insert: {
          attendance_date: string
          consumed_at?: string | null
          expires_at?: string
          field_group_id: string
          granted_at?: string
          granted_by: string
          granted_to: string
          id?: string
          reason: string
          revoked_at?: string | null
        }
        Update: {
          attendance_date?: string
          consumed_at?: string | null
          expires_at?: string
          field_group_id?: string
          granted_at?: string
          granted_by?: string
          granted_to?: string
          id?: string
          reason?: string
          revoked_at?: string | null
        }
        Relationships: [
          {
            foreignKeyName: "rotation_backdate_grants_field_group_id_fkey"
            columns: ["field_group_id"]
            isOneToOne: false
            referencedRelation: "field_groups"
            referencedColumns: ["id"]
          },
        ]
      }
      saved_option_sets: {
        Row: {
          created_at: string
          created_by: string
          id: string
          name: string
          options: Json
        }
        Insert: {
          created_at?: string
          created_by: string
          id?: string
          name: string
          options?: Json
        }
        Update: {
          created_at?: string
          created_by?: string
          id?: string
          name?: string
          options?: Json
        }
        Relationships: []
      }
      service_requests: {
        Row: {
          assigned_to: string | null
          category: string | null
          created_at: string
          description: string
          id: string
          resolution: string | null
          status: string
          student_id: string
          subject: string
          updated_at: string
        }
        Insert: {
          assigned_to?: string | null
          category?: string | null
          created_at?: string
          description: string
          id?: string
          resolution?: string | null
          status?: string
          student_id: string
          subject: string
          updated_at?: string
        }
        Update: {
          assigned_to?: string | null
          category?: string | null
          created_at?: string
          description?: string
          id?: string
          resolution?: string | null
          status?: string
          student_id?: string
          subject?: string
          updated_at?: string
        }
        Relationships: [
          {
            foreignKeyName: "service_requests_student_id_fkey"
            columns: ["student_id"]
            isOneToOne: false
            referencedRelation: "students"
            referencedColumns: ["id"]
          },
        ]
      }
      session_reopen_requests: {
        Row: {
          created_at: string
          decided_at: string | null
          decided_by: string | null
          decision_comment: string | null
          id: string
          reason: string
          requested_by: string
          session_id: string
          status: string
          updated_at: string
        }
        Insert: {
          created_at?: string
          decided_at?: string | null
          decided_by?: string | null
          decision_comment?: string | null
          id?: string
          reason: string
          requested_by: string
          session_id: string
          status?: string
          updated_at?: string
        }
        Update: {
          created_at?: string
          decided_at?: string | null
          decided_by?: string | null
          decision_comment?: string | null
          id?: string
          reason?: string
          requested_by?: string
          session_id?: string
          status?: string
          updated_at?: string
        }
        Relationships: [
          {
            foreignKeyName: "session_reopen_requests_session_id_fkey"
            columns: ["session_id"]
            isOneToOne: false
            referencedRelation: "attendance_sessions"
            referencedColumns: ["id"]
          },
        ]
      }
      session_summaries: {
        Row: {
          absent_count: number
          attendance_rate: number | null
          class: string | null
          closed_at: string | null
          cr_recorded_count: number
          generated_at: string
          generated_by: string | null
          id: string
          module_code: string | null
          pending_approval_count: number
          pending_count: number
          pending_hod_confirm_count: number
          present_count: number
          rejected_count: number
          session_date: string | null
          session_id: string
          topic: string | null
          total_enrolled: number
        }
        Insert: {
          absent_count?: number
          attendance_rate?: number | null
          class?: string | null
          closed_at?: string | null
          cr_recorded_count?: number
          generated_at?: string
          generated_by?: string | null
          id?: string
          module_code?: string | null
          pending_approval_count?: number
          pending_count?: number
          pending_hod_confirm_count?: number
          present_count?: number
          rejected_count?: number
          session_date?: string | null
          session_id: string
          topic?: string | null
          total_enrolled?: number
        }
        Update: {
          absent_count?: number
          attendance_rate?: number | null
          class?: string | null
          closed_at?: string | null
          cr_recorded_count?: number
          generated_at?: string
          generated_by?: string | null
          id?: string
          module_code?: string | null
          pending_approval_count?: number
          pending_count?: number
          pending_hod_confirm_count?: number
          present_count?: number
          rejected_count?: number
          session_date?: string | null
          session_id?: string
          topic?: string | null
          total_enrolled?: number
        }
        Relationships: [
          {
            foreignKeyName: "session_summaries_session_id_fkey"
            columns: ["session_id"]
            isOneToOne: true
            referencedRelation: "attendance_sessions"
            referencedColumns: ["id"]
          },
        ]
      }
      student_accounting_profile: {
        Row: {
          fee_category_id: string | null
          has_medical_equipment: boolean
          has_nhif: boolean
          has_uniform: boolean
          payment_category: string | null
          resides_hostel: boolean
          student_id: string
          updated_at: string
          updated_by: string | null
        }
        Insert: {
          fee_category_id?: string | null
          has_medical_equipment?: boolean
          has_nhif?: boolean
          has_uniform?: boolean
          payment_category?: string | null
          resides_hostel?: boolean
          student_id: string
          updated_at?: string
          updated_by?: string | null
        }
        Update: {
          fee_category_id?: string | null
          has_medical_equipment?: boolean
          has_nhif?: boolean
          has_uniform?: boolean
          payment_category?: string | null
          resides_hostel?: boolean
          student_id?: string
          updated_at?: string
          updated_by?: string | null
        }
        Relationships: [
          {
            foreignKeyName: "student_accounting_profile_fee_category_id_fkey"
            columns: ["fee_category_id"]
            isOneToOne: false
            referencedRelation: "fee_categories"
            referencedColumns: ["id"]
          },
          {
            foreignKeyName: "student_accounting_profile_student_id_fkey"
            columns: ["student_id"]
            isOneToOne: true
            referencedRelation: "students"
            referencedColumns: ["id"]
          },
        ]
      }
      student_biometrics: {
        Row: {
          created_at: string
          fingerprint_path: string | null
          id: string
          photo_path: string | null
          signature_path: string | null
          student_id: string
          updated_at: string
        }
        Insert: {
          created_at?: string
          fingerprint_path?: string | null
          id?: string
          photo_path?: string | null
          signature_path?: string | null
          student_id: string
          updated_at?: string
        }
        Update: {
          created_at?: string
          fingerprint_path?: string | null
          id?: string
          photo_path?: string | null
          signature_path?: string | null
          student_id?: string
          updated_at?: string
        }
        Relationships: [
          {
            foreignKeyName: "student_biometrics_student_id_fkey"
            columns: ["student_id"]
            isOneToOne: true
            referencedRelation: "students"
            referencedColumns: ["id"]
          },
        ]
      }
      student_pre_registrations: {
        Row: {
          class: string
          course: string
          created_at: string
          email: string | null
          form_iv_index: string
          form_iv_results: Json | null
          full_name: string | null
          id: string
          mobile_number: string | null
          nta_level: string | null
          onboarding_status: string
          otp_attempt_count: number
          otp_code: string | null
          otp_expires_at: string | null
          otp_locked_until: string | null
          otp_send_count: number
          otp_send_window_start: string | null
          program: string | null
          semester: string
          updated_at: string
        }
        Insert: {
          class?: string
          course?: string
          created_at?: string
          email?: string | null
          form_iv_index: string
          form_iv_results?: Json | null
          full_name?: string | null
          id?: string
          mobile_number?: string | null
          nta_level?: string | null
          onboarding_status?: string
          otp_attempt_count?: number
          otp_code?: string | null
          otp_expires_at?: string | null
          otp_locked_until?: string | null
          otp_send_count?: number
          otp_send_window_start?: string | null
          program?: string | null
          semester?: string
          updated_at?: string
        }
        Update: {
          class?: string
          course?: string
          created_at?: string
          email?: string | null
          form_iv_index?: string
          form_iv_results?: Json | null
          full_name?: string | null
          id?: string
          mobile_number?: string | null
          nta_level?: string | null
          onboarding_status?: string
          otp_attempt_count?: number
          otp_code?: string | null
          otp_expires_at?: string | null
          otp_locked_until?: string | null
          otp_send_count?: number
          otp_send_window_start?: string | null
          program?: string | null
          semester?: string
          updated_at?: string
        }
        Relationships: []
      }
      student_qr_secrets: {
        Row: {
          created_at: string
          id: string
          qr_type: string
          rotated_at: string
          secret: string
          student_id: string
        }
        Insert: {
          created_at?: string
          id?: string
          qr_type: string
          rotated_at?: string
          secret: string
          student_id: string
        }
        Update: {
          created_at?: string
          id?: string
          qr_type?: string
          rotated_at?: string
          secret?: string
          student_id?: string
        }
        Relationships: [
          {
            foreignKeyName: "student_qr_secrets_student_id_fkey"
            columns: ["student_id"]
            isOneToOne: false
            referencedRelation: "students"
            referencedColumns: ["id"]
          },
        ]
      }
      student_status_history: {
        Row: {
          changed_by: string | null
          created_at: string
          id: string
          new_status: string
          old_status: string | null
          student_id: string
        }
        Insert: {
          changed_by?: string | null
          created_at?: string
          id?: string
          new_status: string
          old_status?: string | null
          student_id: string
        }
        Update: {
          changed_by?: string | null
          created_at?: string
          id?: string
          new_status?: string
          old_status?: string | null
          student_id?: string
        }
        Relationships: [
          {
            foreignKeyName: "student_status_history_student_id_fkey"
            columns: ["student_id"]
            isOneToOne: false
            referencedRelation: "students"
            referencedColumns: ["id"]
          },
        ]
      }
      students: {
        Row: {
          address: string | null
          admission_status: string | null
          barcode_academic: string | null
          barcode_accounting: string | null
          barcode_admin: string | null
          barcode_examination: string | null
          class: string | null
          created_at: string
          current_semester: string | null
          date_of_birth: string | null
          email: string | null
          enrollment_date: string | null
          financial_clearance: boolean
          financial_clearance_status: string
          first_name: string
          form_iv_index: string | null
          gender: string | null
          id: string
          last_name: string
          latitude: number | null
          location_shared_at: string | null
          longitude: number | null
          nactevet_reg_number: string | null
          nida_number: string | null
          nta_level: string | null
          phone: string | null
          program: string | null
          signature_url: string | null
          sit_status: string | null
          status: string
          student_number: string
          updated_at: string
          user_id: string | null
        }
        Insert: {
          address?: string | null
          admission_status?: string | null
          barcode_academic?: string | null
          barcode_accounting?: string | null
          barcode_admin?: string | null
          barcode_examination?: string | null
          class?: string | null
          created_at?: string
          current_semester?: string | null
          date_of_birth?: string | null
          email?: string | null
          enrollment_date?: string | null
          financial_clearance?: boolean
          financial_clearance_status?: string
          first_name: string
          form_iv_index?: string | null
          gender?: string | null
          id?: string
          last_name: string
          latitude?: number | null
          location_shared_at?: string | null
          longitude?: number | null
          nactevet_reg_number?: string | null
          nida_number?: string | null
          nta_level?: string | null
          phone?: string | null
          program?: string | null
          signature_url?: string | null
          sit_status?: string | null
          status?: string
          student_number: string
          updated_at?: string
          user_id?: string | null
        }
        Update: {
          address?: string | null
          admission_status?: string | null
          barcode_academic?: string | null
          barcode_accounting?: string | null
          barcode_admin?: string | null
          barcode_examination?: string | null
          class?: string | null
          created_at?: string
          current_semester?: string | null
          date_of_birth?: string | null
          email?: string | null
          enrollment_date?: string | null
          financial_clearance?: boolean
          financial_clearance_status?: string
          first_name?: string
          form_iv_index?: string | null
          gender?: string | null
          id?: string
          last_name?: string
          latitude?: number | null
          location_shared_at?: string | null
          longitude?: number | null
          nactevet_reg_number?: string | null
          nida_number?: string | null
          nta_level?: string | null
          phone?: string | null
          program?: string | null
          signature_url?: string | null
          sit_status?: string | null
          status?: string
          student_number?: string
          updated_at?: string
          user_id?: string | null
        }
        Relationships: []
      }
      suppressed_emails: {
        Row: {
          created_at: string
          email: string
          id: string
          metadata: Json | null
          reason: string
        }
        Insert: {
          created_at?: string
          email: string
          id?: string
          metadata?: Json | null
          reason: string
        }
        Update: {
          created_at?: string
          email?: string
          id?: string
          metadata?: Json | null
          reason?: string
        }
        Relationships: []
      }
      survey_questions: {
        Row: {
          created_at: string
          id: string
          is_required: boolean
          options: Json | null
          options_sw: Json | null
          question_text: string
          question_text_sw: string | null
          question_type: string
          sort_order: number
          survey_id: string
        }
        Insert: {
          created_at?: string
          id?: string
          is_required?: boolean
          options?: Json | null
          options_sw?: Json | null
          question_text: string
          question_text_sw?: string | null
          question_type?: string
          sort_order?: number
          survey_id: string
        }
        Update: {
          created_at?: string
          id?: string
          is_required?: boolean
          options?: Json | null
          options_sw?: Json | null
          question_text?: string
          question_text_sw?: string | null
          question_type?: string
          sort_order?: number
          survey_id?: string
        }
        Relationships: [
          {
            foreignKeyName: "survey_questions_survey_id_fkey"
            columns: ["survey_id"]
            isOneToOne: false
            referencedRelation: "surveys"
            referencedColumns: ["id"]
          },
        ]
      }
      survey_responses: {
        Row: {
          answers: Json
          created_at: string
          id: string
          respondent_id: string | null
          survey_id: string
        }
        Insert: {
          answers?: Json
          created_at?: string
          id?: string
          respondent_id?: string | null
          survey_id: string
        }
        Update: {
          answers?: Json
          created_at?: string
          id?: string
          respondent_id?: string | null
          survey_id?: string
        }
        Relationships: [
          {
            foreignKeyName: "survey_responses_survey_id_fkey"
            columns: ["survey_id"]
            isOneToOne: false
            referencedRelation: "surveys"
            referencedColumns: ["id"]
          },
        ]
      }
      surveys: {
        Row: {
          created_at: string
          created_by: string
          description: string | null
          description_sw: string | null
          id: string
          inquiry_phone: string | null
          is_public: boolean
          public_slug: string | null
          published_at: string | null
          released_by: string | null
          status: string
          target_roles: string[]
          title: string
          title_sw: string | null
          updated_at: string
        }
        Insert: {
          created_at?: string
          created_by: string
          description?: string | null
          description_sw?: string | null
          id?: string
          inquiry_phone?: string | null
          is_public?: boolean
          public_slug?: string | null
          published_at?: string | null
          released_by?: string | null
          status?: string
          target_roles?: string[]
          title: string
          title_sw?: string | null
          updated_at?: string
        }
        Update: {
          created_at?: string
          created_by?: string
          description?: string | null
          description_sw?: string | null
          id?: string
          inquiry_phone?: string | null
          is_public?: boolean
          public_slug?: string | null
          published_at?: string | null
          released_by?: string | null
          status?: string
          target_roles?: string[]
          title?: string
          title_sw?: string | null
          updated_at?: string
        }
        Relationships: []
      }
      suspicious_qr_scans: {
        Row: {
          created_at: string
          details: Json | null
          id: string
          qr_type: string | null
          reason: string
          resolved_student_id: string | null
          scanned_by: string | null
          scanner_context: string | null
          token_hash: string | null
        }
        Insert: {
          created_at?: string
          details?: Json | null
          id?: string
          qr_type?: string | null
          reason: string
          resolved_student_id?: string | null
          scanned_by?: string | null
          scanner_context?: string | null
          token_hash?: string | null
        }
        Update: {
          created_at?: string
          details?: Json | null
          id?: string
          qr_type?: string | null
          reason?: string
          resolved_student_id?: string | null
          scanned_by?: string | null
          scanner_context?: string | null
          token_hash?: string | null
        }
        Relationships: []
      }
      system_settings: {
        Row: {
          created_at: string
          id: string
          key: string
          updated_at: string
          updated_by: string | null
          value: Json
        }
        Insert: {
          created_at?: string
          id?: string
          key: string
          updated_at?: string
          updated_by?: string | null
          value?: Json
        }
        Update: {
          created_at?: string
          id?: string
          key?: string
          updated_at?: string
          updated_by?: string | null
          value?: Json
        }
        Relationships: []
      }
      user_roles: {
        Row: {
          id: string
          role: Database["public"]["Enums"]["app_role"]
          user_id: string
        }
        Insert: {
          id?: string
          role: Database["public"]["Enums"]["app_role"]
          user_id: string
        }
        Update: {
          id?: string
          role?: Database["public"]["Enums"]["app_role"]
          user_id?: string
        }
        Relationships: []
      }
    }
    Views: {
      modules_public: {
        Row: {
          class: string | null
          id: string | null
          module_code: string | null
          module_name: string | null
          semester: string | null
        }
        Insert: {
          class?: string | null
          id?: string | null
          module_code?: string | null
          module_name?: string | null
          semester?: string | null
        }
        Update: {
          class?: string | null
          id?: string | null
          module_code?: string | null
          module_name?: string | null
          semester?: string | null
        }
        Relationships: []
      }
      student_pre_registrations_exam_safe: {
        Row: {
          class: string | null
          course: string | null
          created_at: string | null
          form_iv_index: string | null
          full_name: string | null
          id: string | null
          nta_level: string | null
          onboarding_status: string | null
          program: string | null
          semester: string | null
        }
        Insert: {
          class?: string | null
          course?: string | null
          created_at?: string | null
          form_iv_index?: string | null
          full_name?: string | null
          id?: string | null
          nta_level?: string | null
          onboarding_status?: string | null
          program?: string | null
          semester?: string | null
        }
        Update: {
          class?: string | null
          course?: string | null
          created_at?: string | null
          form_iv_index?: string | null
          full_name?: string | null
          id?: string | null
          nta_level?: string | null
          onboarding_status?: string | null
          program?: string | null
          semester?: string | null
        }
        Relationships: []
      }
    }
    Functions: {
      admin_override_rotation_attendance: {
        Args: {
          _attendance_date: string
          _field_group_id: string
          _justification: string
          _justification_file_url?: string
          _lat?: number
          _lng?: number
          _radius_m?: number
          _site_id?: string
          _site_name_override?: string
          _status: string
          _student_id: string
        }
        Returns: string
      }
      can_student_view_results: {
        Args: {
          _class?: string
          _exam_type?: string
          _semester?: string
          _student_id: string
        }
        Returns: {
          allowed: boolean
          reason: string
        }[]
      }
      check_rotation_eligibility_alerts: {
        Args: never
        Returns: {
          alerts_created: number
          students_below: number
        }[]
      }
      chitchat_unread_counts: {
        Args: { _user_id: string }
        Returns: {
          conversation_id: string
          unread: number
        }[]
      }
      complete_exam_batch_verification: {
        Args: { _action: string; _notes?: string; _verification_id: string }
        Returns: undefined
      }
      decide_remedial_session_request: {
        Args: { _action: string; _comment?: string; _request_id: string }
        Returns: string
      }
      decide_session_reopen_request: {
        Args: { _action: string; _comment?: string; _request_id: string }
        Returns: undefined
      }
      delete_email: {
        Args: { message_id: number; queue_name: string }
        Returns: boolean
      }
      email_queue_dispatch: { Args: never; Returns: undefined }
      enqueue_email: {
        Args: { payload: Json; queue_name: string }
        Returns: number
      }
      ensure_student_qr_secret: {
        Args: { _qr_type: string; _student_id: string }
        Returns: string
      }
      generate_control_number: {
        Args: { _student_id: string }
        Returns: string
      }
      generate_session_summary: {
        Args: { _session_id: string }
        Returns: {
          absent_count: number
          attendance_rate: number | null
          class: string | null
          closed_at: string | null
          cr_recorded_count: number
          generated_at: string
          generated_by: string | null
          id: string
          module_code: string | null
          pending_approval_count: number
          pending_count: number
          pending_hod_confirm_count: number
          present_count: number
          rejected_count: number
          session_date: string | null
          session_id: string
          topic: string | null
          total_enrolled: number
        }
        SetofOptions: {
          from: "*"
          to: "session_summaries"
          isOneToOne: true
          isSetofReturn: false
        }
      }
      get_my_discipline_cases: {
        Args: never
        Returns: {
          category: string
          created_at: string
          description: string
          hearing_date: string
          id: string
          incident_date: string
          outcome: string
          sanction: string
          severity: string
          status: string
          student_id: string
          updated_at: string
        }[]
      }
      get_own_chitchat_phone: { Args: never; Returns: string }
      get_student_qr_secret_for_self: {
        Args: { _qr_type: string }
        Returns: {
          qr_type: string
          secret: string
          student_id: string
        }[]
      }
      get_user_roles: {
        Args: { _user_id: string }
        Returns: Database["public"]["Enums"]["app_role"][]
      }
      get_users_by_roles: { Args: { _roles: string[] }; Returns: string[] }
      has_role: {
        Args: {
          _role: Database["public"]["Enums"]["app_role"]
          _user_id: string
        }
        Returns: boolean
      }
      has_rotation_backdate_grant: {
        Args: { _date: string; _group_id: string; _user_id: string }
        Returns: boolean
      }
      is_blocked: {
        Args: { _target: string; _viewer: string }
        Returns: boolean
      }
      is_chitchat_admin: {
        Args: { _conversation_id: string; _user_id: string }
        Returns: boolean
      }
      is_chitchat_member: {
        Args: { _conversation_id: string; _user_id: string }
        Returns: boolean
      }
      is_class_rep: { Args: { _user_id: string }; Returns: boolean }
      is_hod_for_class: {
        Args: { _class: string; _user_id: string }
        Returns: boolean
      }
      is_meeting_participant: {
        Args: { _meeting_id: string; _user_id: string }
        Returns: boolean
      }
      is_staff: { Args: { _uid: string }; Returns: boolean }
      is_within_field_site: {
        Args: { _group_id: string; _lat: number; _lng: number }
        Returns: boolean
      }
      log_qr_bypass_scan_start: {
        Args: { _qr_type?: string; _session_id: string }
        Returns: undefined
      }
      lookup_invitation_by_token: {
        Args: { _token: string }
        Returns: {
          email: string
          expires_at: string
          full_name: string
          id: string
          role: Database["public"]["Enums"]["app_role"]
          status: string
        }[]
      }
      lookup_pre_registration: {
        Args: { _form_iv_index: string }
        Returns: {
          form_iv_index: string
          full_name: string
          id: string
          nta_level: string
          onboarding_status: string
          program: string
        }[]
      }
      move_to_dlq: {
        Args: {
          dlq_name: string
          message_id: number
          payload: Json
          source_queue: string
        }
        Returns: number
      }
      notify_survey_published: {
        Args: {
          _is_public: boolean
          _survey_slug_or_id: string
          _survey_title: string
          _target_roles: string[]
        }
        Returns: undefined
      }
      read_email_batch: {
        Args: { batch_size: number; queue_name: string; vt: number }
        Returns: {
          message: Json
          msg_id: number
          read_ct: number
        }[]
      }
      rotate_student_qr_secret: {
        Args: { _qr_type: string; _student_id: string }
        Returns: undefined
      }
      send_bulk_notifications: {
        Args: {
          _link?: string
          _message?: string
          _title: string
          _type?: string
          _user_ids: string[]
        }
        Returns: undefined
      }
      send_notification_to_user: {
        Args: {
          _link?: string
          _message?: string
          _target_user_id: string
          _title: string
          _type?: string
        }
        Returns: undefined
      }
      set_user_online: { Args: { _online: boolean }; Returns: undefined }
    }
    Enums: {
      app_role:
        | "superadmin"
        | "principal"
        | "deputy_principal"
        | "head_admission"
        | "head_administration"
        | "head_academic"
        | "head_examination"
        | "head_accounting"
        | "head_student_service"
        | "head_discipline"
        | "student"
        | "hod_cmt"
        | "hod_pst"
        | "hod_sw"
        | "it_incharge"
        | "security_man"
        | "cr_cmt4"
        | "cr_cmt5"
        | "cr_cmt6"
        | "cr_pst4"
        | "cr_pst5"
        | "cr_pst6"
        | "cr_sw4"
        | "cr_sw5"
        | "cr_sw6"
        | "auditor"
    }
    CompositeTypes: {
      [_ in never]: never
    }
  }
}

type DatabaseWithoutInternals = Omit<Database, "__InternalSupabase">

type DefaultSchema = DatabaseWithoutInternals[Extract<keyof Database, "public">]

export type Tables<
  DefaultSchemaTableNameOrOptions extends
    | keyof (DefaultSchema["Tables"] & DefaultSchema["Views"])
    | { schema: keyof DatabaseWithoutInternals },
  TableName extends DefaultSchemaTableNameOrOptions extends {
    schema: keyof DatabaseWithoutInternals
  }
    ? keyof (DatabaseWithoutInternals[DefaultSchemaTableNameOrOptions["schema"]]["Tables"] &
        DatabaseWithoutInternals[DefaultSchemaTableNameOrOptions["schema"]]["Views"])
    : never = never,
> = DefaultSchemaTableNameOrOptions extends {
  schema: keyof DatabaseWithoutInternals
}
  ? (DatabaseWithoutInternals[DefaultSchemaTableNameOrOptions["schema"]]["Tables"] &
      DatabaseWithoutInternals[DefaultSchemaTableNameOrOptions["schema"]]["Views"])[TableName] extends {
      Row: infer R
    }
    ? R
    : never
  : DefaultSchemaTableNameOrOptions extends keyof (DefaultSchema["Tables"] &
        DefaultSchema["Views"])
    ? (DefaultSchema["Tables"] &
        DefaultSchema["Views"])[DefaultSchemaTableNameOrOptions] extends {
        Row: infer R
      }
      ? R
      : never
    : never

export type TablesInsert<
  DefaultSchemaTableNameOrOptions extends
    | keyof DefaultSchema["Tables"]
    | { schema: keyof DatabaseWithoutInternals },
  TableName extends DefaultSchemaTableNameOrOptions extends {
    schema: keyof DatabaseWithoutInternals
  }
    ? keyof DatabaseWithoutInternals[DefaultSchemaTableNameOrOptions["schema"]]["Tables"]
    : never = never,
> = DefaultSchemaTableNameOrOptions extends {
  schema: keyof DatabaseWithoutInternals
}
  ? DatabaseWithoutInternals[DefaultSchemaTableNameOrOptions["schema"]]["Tables"][TableName] extends {
      Insert: infer I
    }
    ? I
    : never
  : DefaultSchemaTableNameOrOptions extends keyof DefaultSchema["Tables"]
    ? DefaultSchema["Tables"][DefaultSchemaTableNameOrOptions] extends {
        Insert: infer I
      }
      ? I
      : never
    : never

export type TablesUpdate<
  DefaultSchemaTableNameOrOptions extends
    | keyof DefaultSchema["Tables"]
    | { schema: keyof DatabaseWithoutInternals },
  TableName extends DefaultSchemaTableNameOrOptions extends {
    schema: keyof DatabaseWithoutInternals
  }
    ? keyof DatabaseWithoutInternals[DefaultSchemaTableNameOrOptions["schema"]]["Tables"]
    : never = never,
> = DefaultSchemaTableNameOrOptions extends {
  schema: keyof DatabaseWithoutInternals
}
  ? DatabaseWithoutInternals[DefaultSchemaTableNameOrOptions["schema"]]["Tables"][TableName] extends {
      Update: infer U
    }
    ? U
    : never
  : DefaultSchemaTableNameOrOptions extends keyof DefaultSchema["Tables"]
    ? DefaultSchema["Tables"][DefaultSchemaTableNameOrOptions] extends {
        Update: infer U
      }
      ? U
      : never
    : never

export type Enums<
  DefaultSchemaEnumNameOrOptions extends
    | keyof DefaultSchema["Enums"]
    | { schema: keyof DatabaseWithoutInternals },
  EnumName extends DefaultSchemaEnumNameOrOptions extends {
    schema: keyof DatabaseWithoutInternals
  }
    ? keyof DatabaseWithoutInternals[DefaultSchemaEnumNameOrOptions["schema"]]["Enums"]
    : never = never,
> = DefaultSchemaEnumNameOrOptions extends {
  schema: keyof DatabaseWithoutInternals
}
  ? DatabaseWithoutInternals[DefaultSchemaEnumNameOrOptions["schema"]]["Enums"][EnumName]
  : DefaultSchemaEnumNameOrOptions extends keyof DefaultSchema["Enums"]
    ? DefaultSchema["Enums"][DefaultSchemaEnumNameOrOptions]
    : never

export type CompositeTypes<
  PublicCompositeTypeNameOrOptions extends
    | keyof DefaultSchema["CompositeTypes"]
    | { schema: keyof DatabaseWithoutInternals },
  CompositeTypeName extends PublicCompositeTypeNameOrOptions extends {
    schema: keyof DatabaseWithoutInternals
  }
    ? keyof DatabaseWithoutInternals[PublicCompositeTypeNameOrOptions["schema"]]["CompositeTypes"]
    : never = never,
> = PublicCompositeTypeNameOrOptions extends {
  schema: keyof DatabaseWithoutInternals
}
  ? DatabaseWithoutInternals[PublicCompositeTypeNameOrOptions["schema"]]["CompositeTypes"][CompositeTypeName]
  : PublicCompositeTypeNameOrOptions extends keyof DefaultSchema["CompositeTypes"]
    ? DefaultSchema["CompositeTypes"][PublicCompositeTypeNameOrOptions]
    : never

export const Constants = {
  public: {
    Enums: {
      app_role: [
        "superadmin",
        "principal",
        "deputy_principal",
        "head_admission",
        "head_administration",
        "head_academic",
        "head_examination",
        "head_accounting",
        "head_student_service",
        "head_discipline",
        "student",
        "hod_cmt",
        "hod_pst",
        "hod_sw",
        "it_incharge",
        "security_man",
        "cr_cmt4",
        "cr_cmt5",
        "cr_cmt6",
        "cr_pst4",
        "cr_pst5",
        "cr_pst6",
        "cr_sw4",
        "cr_sw5",
        "cr_sw6",
        "auditor",
      ],
    },
  },
} as const
